import React, { ReactElement } from 'react'
import styled from 'styled-components'
import { Heading, MoreInfoLink, Text } from '../../general'
import { Divider } from 'antd'
import { theme } from '../../config/theme'

type FormCardProps = {
  title?: string | React.ReactNode
  subtitle?: string
  helpArticleUrl?: string
  children?: React.ReactNode
  rightChildren?: React.ReactNode
  leftChildren?: React.ReactNode
  level?: 1 | 2
  className?: string
  padding?: string
  id?: string
  testId?: string
}

const FormCard = ({
  title,
  subtitle,
  helpArticleUrl,
  children,
  rightChildren,
  level = 1,
  className,
  leftChildren,
  padding,
  id,
  testId
}: FormCardProps): ReactElement => {
  const backgroundColor =
    level === 1 ? theme.color.neutral.color11 : theme.color.neutral.color10

  const hasChildren = React.Children.count(children) > 0

  const isStringTitle = typeof title === 'string'

  return (
    <CardContainer
      $backgroundColor={backgroundColor}
      className={className}
      id={id || undefined}
      data-testid={testId}
    >
      <HeaderRowContainer>
        {leftChildren}
        <HeaderMainContainer>
          {title && isStringTitle && (
            <HeaderContainer>
              <TitleContainer>
                {level === 1 && (
                  <FormCardHeading level={3}>{title}</FormCardHeading>
                )}
                {level === 2 && (
                  <FormCardHeading level={4}>{title}</FormCardHeading>
                )}
                {helpArticleUrl && (
                  <MoreInfoLink helpArticleUrl={helpArticleUrl} />
                )}
              </TitleContainer>
              {rightChildren}
            </HeaderContainer>
          )}
          {title && !isStringTitle && (
            <HeaderContainer>
              {title}
              {rightChildren}
            </HeaderContainer>
          )}
          {title && subtitle && <Subtitle>{subtitle}</Subtitle>}
        </HeaderMainContainer>
      </HeaderRowContainer>
      {title && hasChildren && <StyledDivider />}
      {hasChildren && <FormChildren>{children}</FormChildren>}
    </CardContainer>
  )
}

const CardContainer = styled.div<{
  $backgroundColor: string
  $padding?: string
}>`
  padding: ${(props) => props?.$padding || '24px'};
  border-radius: 20px;
  background-color: ${(props) => props?.$backgroundColor};
  display: flex;
  flex-direction: column;
  justify-content: center;
`

const FormChildren = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`

const HeaderMainContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`

const HeaderRowContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const FormCardHeading = styled(Heading)`
  color: ${({ theme }) => theme.color.neutral.color4};
`

const Subtitle = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color4};
`

const StyledDivider = styled(Divider)`
  color: ${({ theme }) => theme.color.neutral.color8};
  margin: 16px 0 24px 0;
`

export default FormCard
